import React, { useState, useRef, useEffect } from "react";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import MomentumSmallImg from "../assets/images/MSmallLogo.svg";
import CommonValues from "../Common/utils";
import { Skeleton } from "primereact/skeleton";

interface AskAlexandriaDialogProps {
  visible: boolean;
  onHide: () => void;
  id: number;
}

interface Message {
  type: "user" | "bot";
  text: string;
}

export default function AskAlexandriaDialog({
  visible,
  onHide,
  id,
}: AskAlexandriaDialogProps) {
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const chatRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const suggestedPrompts = [
    "Create a summary for this call log.",
    "what this conversation is about?",
    "Summarize the key points from this transcript.",
    "What were the main action items discussed?",
    "Highlight any important decisions made",
  ];

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, streamingText]);

  useEffect(() => {
    if (visible) {
      setMessages([]);
      setQuestion("");
      setLoading(false);
      setStreamingText("");
    }
  }, [visible]);

  const handlePromptClick = (prompt: string) => {
    if (prompt === "Message Momentum AI...") {
      setQuestion("");
      textareaRef.current?.focus();
      return;
    }
    setQuestion(prompt);
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const handleAsk = async () => {
    if (!question.trim()) return;

    const userMsg: Message = { type: "user", text: question };
    setMessages((prev) => [...prev, userMsg]);
    setQuestion("");
    setLoading(true);
    setStreamingText("");

    try {
      const token = CommonValues.GetToken();
      const formData = new URLSearchParams();
      formData.append("id", id.toString());
      formData.append("question", question);

      const response = await fetch(
        `https://alexandriaai-stage.insurancegig.com/api/alexandria-ai/embedding`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData.toString(),
        }
      );

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No reader available");

      const decoder = new TextDecoder();
      let fullAnswer = "";
      let buffer = "";

      const processChunk = async (text: string) => {
        buffer += text;
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const jsonStr = line.substring(6).trim();
              if (!jsonStr) continue;

              const data = JSON.parse(jsonStr);
              if (!data.answer) continue;
              const newText = data.answer.slice(fullAnswer.length);
              fullAnswer = data.answer;

              for (let i = 0; i < newText.length; i++) {
                setStreamingText((prev) => prev + newText[i]);
                await new Promise((resolve) => setTimeout(resolve, 15));
              }
            } catch (e) {}
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        await processChunk(decoder.decode(value, { stream: true }));
      }
      if (buffer) await processChunk("");
      if (fullAnswer) {
        setMessages((prev) => [...prev, { type: "bot", text: fullAnswer }]);
      }
    } catch (error) {
      console.error("Error:", error);
      setMessages((prev) => [
        ...prev,
        { type: "bot", text: "Sorry, something went wrong. Please try again." },
      ]);
    } finally {
      setLoading(false);
      setStreamingText("");
    }
  };

  const onEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="alexandria-floating-dialog"
          initial={{ opacity: 0, y: 100, x: 100 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{ opacity: 0, y: 100, x: 100 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
        >
          <div className="alexandria-header">
            <span className="ms-2">Ask Alexandria</span>
            <Button
              icon="pi pi-times"
              className="p-button-text"
              onClick={onHide}
            />
          </div>

          <div className="alexandria-chat" ref={chatRef}>
            {messages.length === 0 ? (
              <div className="Alexprompts-container">
                <div className="Alexsuggested-prompts-title">
                  How may I assist you today?
                </div>
                <div className="Alexsuggested-prompts-grid">
                  {suggestedPrompts.map((prompt, index) => (
                    <div
                      key={index}
                      className={`Alexprompt-card ${
                        prompt === "Message Momentum AI..." ? "action-card" : ""
                      }`}
                      onClick={() => handlePromptClick(prompt)}
                    >
                      {prompt}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, idx) => (
                  <div key={idx} className={`chat-msg ${msg.type}`}>
                    {msg.type === "bot" && (
                      <img
                        src={MomentumSmallImg}
                        alt="Bot"
                        className="chat-avatar"
                      />
                    )}
                    <div className={`chat-bubble ${msg.type}`}>
                      <ReactMarkdown>{msg.text}</ReactMarkdown>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="chat-msg bot">
                    <img
                      src={MomentumSmallImg}
                      alt="Bot"
                      className="chat-avatar"
                    />
                    <div className="chat-bubble bot">
                      {streamingText ? (
                        <ReactMarkdown>{streamingText}</ReactMarkdown>
                      ) : (
                        <div className="typing-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="alexandria-input-area">
            <InputTextarea
              ref={textareaRef}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={onEnter}
              rows={1}
              autoResize
              className="alexandria-textarea"
              placeholder="How can I help you today?"
              disabled={loading}
            />
            <Button
              icon="pi pi-send"
              className="alexandria-send-btn"
              onClick={handleAsk}
              disabled={loading || !question.trim()}
              aria-label="Send"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
